/**
 * projection.ts — the weight-projection maths and its mandatory disclaimer.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.1 (patient journey).
 *
 * WHY: the projection interstitial and the assessment result screen both show
 * the same illustrative curve — round(weightKg × 0.21) lost over 10 monthly
 * points, then a flat maintenance tail. The formula and the disclaimer live
 * here so the two screens can never drift apart, and so the spec suite can
 * test the maths without rendering. The disclaimer is MANDATORY wherever the
 * curve renders (spec: "illustrative, not a guarantee").
 */
import { round1 } from './units';

/** Months from start to the projected goal weight. */
export const PROJECTION_MONTHS = 10;

/**
 * The mandatory wording shown under every projection chart. Category-level
 * copy only (R28) — it names no product and promises nothing.
 */
export const PROJECTION_DISCLAIMER =
  'Illustrative projection from published clinical trial averages. ' +
  'Your own result will be individual — it is not a guarantee.';

/** Projected loss in whole kg: round(weightKg × 0.21) (spec formula). */
export function projectedLossKg(weightKg: number): number {
  return Math.round(weightKg * 0.21);
}

/** Projected goal weight in kg after {@link PROJECTION_MONTHS} months. */
export function goalWeightKg(weightKg: number): number {
  return weightKg - projectedLossKg(weightKg);
}

/**
 * The curve: 11 values (month 0 → month 10), easing quadratically from the
 * starting weight down to the goal — faster loss early, levelling off —
 * matching the prototype's chart. The flat maintenance tail after month 10
 * is drawn by the chart, anchored at the final value here.
 */
export function projectionSeries(weightKg: number): number[] {
  const loss = projectedLossKg(weightKg);
  return Array.from({ length: PROJECTION_MONTHS + 1 }, (_, month) => {
    const t = month / PROJECTION_MONTHS;
    const ease = 1 - Math.pow(1 - t, 2);
    return round1(weightKg - loss * ease);
  });
}

/** Client-side BMI (kg/m²) — display only; the server recomputes authoritatively. */
export function bmiOf(heightCm: number, weightKg: number): number {
  const metres = heightCm / 100;
  return weightKg / (metres * metres);
}
